import { useState } from "react";

const FORM_ADDR =
  (import.meta.env.VITE_PUBLIC_FORM_ENDPOINT as string) || "/forms/contact.php";

const INVEST_TYPES = [
  "Type 1 Cluster Villa — ₹29.9 Lakhs",
  "Type 2 Cluster Villa — ₹39.9 Lakhs",
  "Type 3 Premium Duplex Villa — ₹59.9 Lakhs",
  "Type 4 Luxury Duplex Villa — ₹99 Lakhs",
  "Nomad A-Shape Cottage",
  "Not sure yet",
];

const MODELS = ["Profit Sharing Model", "Minimum Rental Model", "Need guidance"];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check: if filled, silently treat as success without sending
    const honeypot = (formData.get("website_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      return;
    }

    // Omit empty honeypot from payload
    formData.delete("website_alt");

    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
        return;
      }

      setStatus("error");
      setFormError(
        serverMsg && typeof serverMsg === "string" && serverMsg.trim()
          ? serverMsg
          : "We couldn't submit your request. Please try again."
      );
    } catch {
      setStatus("error");
      setFormError("Something went wrong. Please try again or call us directly.");
    }
  }

  return (
    <section id="contact" className="relative w-full py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-14 items-start">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary-600">
              <i className="ri-mail-send-line"></i> Get Started
            </span>
            <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 mt-4 leading-tight">
              Schedule Your <span className="italic text-primary-700">Site Visit</span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mt-4 leading-relaxed">
              Tell us a little about your interest and our investment team will get in touch to
              arrange a private site visit and discuss the right option for you.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href="tel:8088330330"
                className="flex items-center gap-4 rounded-2xl bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-phone-fill text-xl"></i>
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-600">Call / WhatsApp</span>
                  <span className="block text-base font-bold text-foreground-950">80883 30330</span>
                </span>
              </a>
              <a
                href="https://www.nomadlivings.com"
                target="_blank"
                rel="nofollow noopener"
                className="flex items-center gap-4 rounded-2xl bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-global-line text-xl"></i>
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-600">Website</span>
                  <span className="block text-base font-bold text-foreground-950">www.nomadlivings.com</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl bg-background-50 border border-background-200 p-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-map-pin-2-line text-xl"></i>
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-600">Location</span>
                  <span className="block text-base font-bold text-foreground-950">Prime Tourism Destination, India</span>
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-background-50 border border-background-200 p-5 md:p-8">
            <h3 className="font-heading text-2xl text-foreground-950">Investor Interest Form</h3>
            <p className="text-sm text-foreground-600 mt-1">
              Fill this in and our team will reach out shortly.
            </p>

            {status === "success" ? (
              <div className="mt-6 rounded-2xl bg-primary-50 border border-primary-200 p-6 text-center">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-check-line text-2xl"></i>
                </span>
                <p className="font-heading text-xl text-foreground-950 mt-4">Thank You!</p>
                <p className="text-sm text-foreground-600 mt-2">
                  Your enquiry has been received. Our investment team will contact you shortly to
                  arrange your site visit.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-5 text-sm font-semibold text-primary-700 hover:text-primary-800"
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="mt-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Full Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="Your name"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Phone / WhatsApp
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="+91 00000 00000"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="investment_type" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Investment of Interest
                  </label>
                  <select
                    id="investment_type"
                    name="investment_type"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    {INVEST_TYPES.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="model" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Preferred Revenue Model
                  </label>
                  <select
                    id="model"
                    name="model"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    {MODELS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Message <span className="text-foreground-500 font-normal">(optional)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us a bit about your investment goals..."
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 text-sm md:text-base font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3.5 hover:bg-primary-700 transition-colors"
                >
                  Request Site Visit
                  <i className="ri-arrow-right-line"></i>
                </button>
                <p className="text-center text-[11px] text-foreground-600">
                  We respect your privacy. Your details are only used to arrange your visit.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}